import { equals } from '@execonline-inc/collections';
import { toTask, when } from '@execonline-inc/maybe-adapter';
import { assertNever, noop } from '@kofno/piper';
import { fromNullable, Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { currentUserStore } from '../../../../CurrentUserStore';
import { emptyFragment } from '../../../../EmptyFragment';
import { findLink } from '../../../../LinkyLinky';
import { Link } from '../../../../Resource/Types';
import { T } from '../../../../Translations';
import { LearningCollectionResource } from '../../Types';
import Nav from '../Nav';
import ShareButton from '../Share/ShareButton';
import ShareOptionsModal from '../Share/ShareOptionsModal';
import BreadCrumbs from './BreadCrumbs';
import InvitationsStore from './Store';

// DVLP-7041: admins who get Shareable Links share through the Share button; the
// legacy invite modal stays for admins who keep Program Access.
const legacyInviteLink = (collectionLinks: ReadonlyArray<Link>): Maybe<Link> =>
  when(findLink('learning-collection-deep-links', currentUserStore.links).isNothing(), {}).andThen(
    () => findLink('invite', collectionLinks),
  );

interface Props {
  learningCollectionResource: LearningCollectionResource;
  open: () => void;
  invitationsStore: InvitationsStore;
}

function InvitationsHeader({ learningCollectionResource, open, invitationsStore }: Props) {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const whenNavigatedFromAiCollectionBuilder = (): Maybe<string> =>
    fromNullable(sessionStorage.getItem('learningCollectionState')).andThen((value) =>
      when(equals(value, 'show'), 'show'),
    );

  useEffect(() => {
    switch (invitationsStore.state.kind) {
      case 'error':
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'sorting':
      case 'updating-query':
      case 'updating-search-parameters':
        break;
      case 'ready':
        toTask(
          { kind: 'not-navigated-from-ai-collection-builder' },
          whenNavigatedFromAiCollectionBuilder().andThen(() =>
            legacyInviteLink(learningCollectionResource.links),
          ),
        ).fork(noop, () => {
          open();
          sessionStorage.removeItem('learningCollectionState');
        });
        break;
      default:
        return assertNever(invitationsStore.state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open is an unmemoized callback prop; including it re-triggers the sessionStorage check on every parent render
  }, [invitationsStore.state]);
  return (
    <>
      <div className="flex border-b-1 border-gray-300 px-2.5 pt-2.5 lg:px-12 lg:pt-4 flex-col bg-white">
        <BreadCrumbs learningCollectionName={learningCollectionResource.payload.name} />
        <div className="flex flex-col">
          <h2 className="text-xl font-semibold text-gray-900">
            <T kind="Manage Participant Access" />
          </h2>
          <Nav id={learningCollectionResource.payload.id} />
        </div>
      </div>
      <div className="flex justify-between items-center pb-4 pt-8 bg-gray-50 px-2.5 lg:px-12 ">
        <h2 className="text-xl font-semibold text-gray-900">
          <T kind="Invited Participants" />
        </h2>
        <div className="flex items-center gap-3">
          <ShareButton
            onClick={() => setIsInviteModalOpen(true)}
            collectionLinks={learningCollectionResource.links}
          />
          {legacyInviteLink(learningCollectionResource.links)
            .map(() => (
              <button
                id="invite-participants-button"
                className="rounded-md bg-teal-600 px-3 py-2 text-white"
                onClick={open}
              >
                <T kind="Invite Participants" />
              </button>
            ))
            .getOrElse(emptyFragment)}
        </div>
      </div>
      {isInviteModalOpen && (
        <ShareOptionsModal
          close={() => setIsInviteModalOpen(false)}
          collectionId={learningCollectionResource.payload.id}
          publishedAt={learningCollectionResource.payload.publishedAt}
          onPublishSuccess={(newPublishedAt) => {
            learningCollectionResource.payload.publishedAt = newPublishedAt;
          }}
          returnPath={`/learning_collections/${learningCollectionResource.payload.id}/invitations`}
        />
      )}
    </>
  );
}

export default observer(InvitationsHeader);
